import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { sanitizeTokenizedPath } from '../utils/tokenized-path';
import { ENV_NAME, SENTRY_DSN } from './env';
import { isDroppableClientError } from './sentry-filters';

const dsn = SENTRY_DSN;
const environment = ENV_NAME;
const isProduction = environment === 'production';

const TRACES_SAMPLE_RATE = isProduction ? 0.2 : 0.0;
const REPLAY_SESSION_RATE = isProduction ? 0.1 : 0.0;
const REPLAY_ON_ERROR_RATE = isProduction ? 1.0 : 0.0;

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/app\.prospero\.food\/api/,
      /^https:\/\/prospero\.queuespark\.com\/api/,
    ],
    replaysSessionSampleRate: REPLAY_SESSION_RATE,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_RATE,
    beforeSend(event, hint) {
      if (isDroppableClientError(hint?.originalException)) {
        return null;
      }
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
      }
      if (event.request?.headers) {
        delete event.request.headers['x-forwarded-for'];
        delete event.request.headers['x-real-ip'];
        delete event.request.headers.cookie;
        delete event.request.headers.authorization;
      }
      // I path con token segreto (link referral, inviti) non devono finire nel
      // crash report: placeholder letterale al posto del segmento variabile.
      // Base origin per gli URL relativi (xhr/fetch loggano spesso solo il path):
      // senza base il costruttore lancia e il segreto passerebbe intatto.
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url, window.location.origin);
          url.pathname = sanitizeTokenizedPath(url.pathname);
          event.request.url = url.toString();
        } catch {
          // ignore invalid URLs
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.url) {
        try {
          const url = new URL(breadcrumb.data.url, window.location.origin);
          url.search = '';
          url.pathname = sanitizeTokenizedPath(url.pathname);
          breadcrumb.data.url = url.toString();
        } catch {
          // ignore invalid URLs
        }
      }
      // I breadcrumb di navigazione portano from/to (non data.url): i path con
      // token passano da lì.
      if (breadcrumb.data) {
        for (const key of ['from', 'to'] as const) {
          const value = breadcrumb.data[key];
          if (typeof value === 'string') {
            breadcrumb.data[key] = sanitizeTokenizedPath(value);
          }
        }
      }
      if (
        breadcrumb.message &&
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(breadcrumb.message)
      ) {
        breadcrumb.message = breadcrumb.message.replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          '[email-redacted]',
        );
      }
      return breadcrumb;
    },
  });

  if (typeof window !== 'undefined' && (REPLAY_SESSION_RATE > 0 || REPLAY_ON_ERROR_RATE > 0)) {
    const attach = (): void => {
      void import('./sentry-replay')
        .then((m) => m.attachReplay())
        .catch(() => {
          // ignore errors during replay initialization
        });
    };
    const schedule = (): void => {
      const idle = (window as Window & typeof globalThis).requestIdleCallback;
      if (typeof idle === 'function') {
        idle(attach);
      } else {
        setTimeout(attach, 2000);
      }
    };
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }
}

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
